import { Component, OnInit } from '@angular/core'
import { Auth } from '@angular/fire/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  QuerySnapshot,
  setDoc,
} from '@angular/fire/firestore'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute } from '@angular/router'
import { map, switchMap } from 'rxjs/operators'
import { Dog } from 'src/domain/dog'
import { TrainingEntry, TrainingType } from 'src/domain/training'

export interface TrainingEntryItem extends TrainingEntry {
  docId: string
}

@Component({
    templateUrl: './training.component.html',
    styleUrls: ['./training.component.scss'],
    standalone: false
})
export class TrainingComponent implements OnInit {
  constructor(private store: Firestore, private route: ActivatedRoute, private snack: MatSnackBar, private auth: Auth) {}

  entries: TrainingEntryItem[] = []
  dogId: string
  dog: Dog
  loading = false

  trainingTypes: TrainingType[] = []

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((param) => {
          this.dogId = param.get('dogId')
          return this.dogId
        }),
        switchMap((dogId) => getDoc(doc(this.store, 'dogs', dogId)))
      )
      .subscribe({
        next: (dog) => {
          this.dog = dog.data() as Dog
          this.refreshEntries()
        },
        error: (err) => console.error(err),
      })

    this.readTrainingTypes()
  }

  private readTrainingTypes() {
    getDocs(collection(this.store, 'training-types')).then(
      (snap: QuerySnapshot<DocumentData>) => (this.trainingTypes = snap.docs.map((d) => d.data() as TrainingType))
    )
  }

  private refreshEntries() {
    return this.getAllEntries(this.dogId).then(
      (data: QuerySnapshot<DocumentData>) => {
        this.entries = data.docs.map((docSnap) => {
          const d = docSnap.data() as TrainingEntry
          return {
            docId: docSnap.id,
            date: (d.date as any).toDate(),
            comment: d.comment,
            progress: d.progress,
            type: d.type,
            createdAt: (d.createdAt as any).toDate(),
            createdBy: d.createdBy,
          }
        })
      },
      (err) => console.error(err)
    )
  }

  private getAllEntries(dogId: string) {
    return getDocs(query(collection(this.getDogDoc(dogId), 'training'), orderBy('date', 'desc')))
  }

  private getDogDoc(dogId: string) {
    return doc(this.store, 'dogs', dogId)
  }

  private getTrainingDoc(dogId: string, trainingId: string) {
    return doc(this.getDogDoc(dogId), 'training', trainingId)
  }

  async saveEntry(item: TrainingEntryItem) {
    this.loading = true
    try {
      const firebaseEntry = await this.convertEntryItemToItem(item)
      if (item.docId) {
        console.log('Updating item', firebaseEntry)
        await setDoc(this.getTrainingDoc(this.dogId, item.docId), firebaseEntry)
      } else {
        console.log('Creating new entry', firebaseEntry)
        await addDoc(collection(this.getDogDoc(this.dogId), 'training'), firebaseEntry)
      }
      this.refreshEntries()
      this.snack.open(`Edzés ${item.date.toLocaleDateString()} sikeresen frissítve`, 'Ok', { duration: 2000 })
    } catch (e) {
      console.error(e)
      this.snack.open(`Nem sikerült frissíteni a ${item.date?.toLocaleDateString()} bejegyzést: ${e.code} - ${e.message}`, 'Francba')
    } finally {
      this.loading = false
    }
  }

  private async convertEntryItemToItem(item: TrainingEntryItem): Promise<TrainingEntry> {
    const email = this.auth.currentUser.email
    return {
      date: item.date,
      type: item.type,
      progress: item.progress,
      comment: item.comment || null,
      createdAt: new Date(),
      createdBy: email,
    }
  }

  async deleteEntry(docId: string) {
    if (docId) {
      this.loading = true
      try {
        await deleteDoc(this.getTrainingDoc(this.dogId, docId))
        this.refreshEntries()
        this.snack.open('Bejegyzés törölve', 'Ok', { duration: 2000 })
      } catch (e) {
        console.error(e)
        this.snack.open(`Nem lehetett törölni a ${docId} bejegyzést: ${e.code} - ${e.message}`, 'Francba')
      } finally {
        this.loading = false
      }
    }
  }
}
